/**
 * Cart API
 * ────────────────────────────────────────────────────────────────────────
 * Universal cart operations layer. Single source of truth for all cart
 * API calls. Works with any manager/UI that consumes the returned data.
 *
 * Features:
 *   • Request queuing (serializes operations)
 *   • Abort control (cancels in-flight requests by key)
 *   • Section rendering (surgical DOM updates)
 *   • Event dispatching (cart:updated, cart:error, etc.)
 *
 * Usage:
 *   const api = new CartAPI({ sections: ['mini-cart-header', 'cart-template'] });
 *   await api.updateItemQty(2, 5);  // line 2, qty 5
 *
 * @class CartAPI
 * @param {Object} options - Configuration
 * @param {string[]} options.sections - Section names to fetch in responses
 * @param {string} options.sectionsUrl - URL to fetch sections from (default: '/cart')
 */
class CartAPI {
  constructor({ sections = [], sectionsUrl = '/cart' } = {}) {
    this.root = window.Shopify?.routes?.root || '/';
    this.sections = sections;
    this.sectionsUrl = sectionsUrl;
    this.inflight = new Map(); // Map<key, AbortController>

    console.log('[CartAPI] Initialized with sections:', this.sections);
  }

  // ─── Request Layer ────────────────────────────────────────────────────

  /**
   * Internal request handler. Manages fetch, error handling, and event dispatch.
   * @private
   * @param {string} endpoint - Cart endpoint (add, change, clear, update, etc.)
   * @param {Object} body - Request payload
   * @param {string} [key] - Line item key for request tracking/abort
   * @returns {Promise<Object>} Cart data from Shopify API
   * @throws {Error} If request fails
   */
  async _request(endpoint, body = {}, { key } = {}) {
    // Abort any previous in-flight request for this key to prevent race conditions
    if (key && this.inflight.has(key)) {
      console.debug(`[CartAPI] Aborting previous request for key: ${key}`);
      this.inflight.get(key).abort();
    }

    const controller = new AbortController();
    if (key) this.inflight.set(key, controller);

    const payload = { ...body };
    // Include section names in request so Shopify returns their HTML
    if (this.sections.length) {
      payload.sections = this.sections.join(',');
      payload.sections_url = this.sectionsUrl;
    }

    console.log(`[CartAPI] Request started`, {
      endpoint,
      key,
      payload,
      url: `${this.root}cart/${endpoint}.js`,
    });

    document.dispatchEvent(
      new CustomEvent('cart:request:start', { detail: { endpoint, key } })
    );

    try {
      const res = await fetch(`${this.root}cart/${endpoint}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const data = await res.json();

      if (!res.ok) {
        console.error(`[CartAPI] Request failed`, {
          endpoint,
          key,
          status: res.status,
          error: data,
        });
        throw Object.assign(
          new Error(data.description || data.message || 'Cart error'),
          { status: res.status, body: data }
        );
      }

      console.log(`[CartAPI] Request succeeded`, {
        endpoint,
        key,
        cart: data,
        sectionsReturned: Object.keys(data?.sections || {}),
      });

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: data, sections: data?.sections, source: 'api' },
        })
      );

      return data;
    } catch (err) {
      if (err.name === 'AbortError') {
        console.debug(`[CartAPI] Request aborted for key: ${key}`);
        return null;
      }

      console.error(`[CartAPI] Request error`, {
        endpoint,
        key,
        error: err.message,
        status: err.status,
      });

      document.dispatchEvent(
        new CustomEvent('cart:error', {
          detail: { endpoint, key, error: err },
        })
      );

      throw err;
    } finally {
      if (key && this.inflight.get(key) === controller) {
        this.inflight.delete(key);
      }
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────

  /**
   * Fetch current cart data (read-only, no mutation)
   * @returns {Promise<Object>} Cart object with items, totals, etc.
   */
  getCart() {
    console.log('[CartAPI] Fetching cart...');
    return fetch(`${this.root}cart.js`).then((r) => r.json());
  }

  /**
   * Add item(s) to cart
   * @param {number|string} variantId - Shopify variant ID
   * @param {number} [qty=1] - Quantity to add
   * @param {Object} [opts] - Additional properties (e.g., properties, selling_plan_id)
   * @returns {Promise<Object>} Updated cart data
   */
  addItem(variantId, qty = 1, opts) {
    console.log(`[CartAPI] Adding item: variantId=${variantId}, qty=${qty}`, opts);
    return this._request('add', {
      items: [{ id: variantId, quantity: qty, ...opts }],
    });
  }

  /**
   * Update quantity for a line item (or remove if qty=0)
   * @param {number|string} key - Line item number (1, 2, 3...) or line item key
   * @param {number} qty - New quantity (0 to remove)
   * @returns {Promise<Object>} Updated cart data
   */
  updateItemQty(key, qty) {
    console.log(`[CartAPI] Updating qty: line=${key}, qty=${qty}`);
    return this._request('change', { line: key, quantity: qty }, { key });
  }

  /**
   * Remove item from cart (convenience for updateItemQty with qty=0)
   * @param {number|string} key - Line item number
   * @returns {Promise<Object>} Updated cart data
   */
  removeItem(key) {
    console.log(`[CartAPI] Removing item: line=${key}`);
    return this.updateItemQty(key, 0);
  }

  /**
   * Batch update multiple line items at once
   * @param {Object} updates - Map of line keys to new quantities
   * @example updateItems({ 1: 5, 2: 0 })  // Set line 1 to qty 5, remove line 2
   * @returns {Promise<Object>} Updated cart data
   */
  updateItems(updates) {
    console.log(`[CartAPI] Batch updating items:`, updates);
    return this._request('update', { updates });
  }

  /**
   * Update cart note (appears at checkout)
   * @param {string} note - Note text
   * @returns {Promise<Object>} Updated cart data
   */
  updateNote(note) {
    console.log(`[CartAPI] Updating note: "${note.substring(0, 50)}..."`);
    return this._request('update', { note });
  }

  /**
   * Clear all items from cart
   * @returns {Promise<Object>} Empty cart data
   */
  clearCart() {
    console.log('[CartAPI] Clearing cart');
    return this._request('clear');
  }
}

if (!window.cartAPI) {
  window.cartAPI = new CartAPI();
  console.log('[CartAPI] Global instance created: window.cartAPI');
  // Source map: assets/cart-api.js
  // For debugging: inspect network requests, events, and logs with [CartAPI] prefix
}

// ─── Source Map ───────────────────────────────────────────────────────────
// @sourceMappingURL=cart-api.js.map
// Debug with: Open DevTools → Console → Filter by "[CartAPI]"

/* --- MOQ UX: global toast for cart errors (added 2026-08-25) ---------------
 * CartAPI dispatches `cart:error` for failed cart mutations (quick-add,
 * drawer qty changes). Nothing displayed those errors outside the PDP form,
 * so checkout-validation blocks (e.g. MOQ minimums) failed silently. This
 * listener shows a dismissable toast unless an inline product-form error is
 * already visible. */
(function () {
  if (window.__iacCartErrorToast) return;
  window.__iacCartErrorToast = true;

  var css = '.iac-cart-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);' +
    'background:#b42318;color:#fff;padding:12px 20px;border-radius:8px;font-size:14px;' +
    'z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,.25);max-width:90vw;text-align:center;' +
    'opacity:0;transition:opacity .2s}.iac-cart-toast.visible{opacity:1}';
  var style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  var hideTimer = null;
  document.addEventListener('cart:error', function (evt) {
    var msg = evt.detail && evt.detail.error && evt.detail.error.message;
    if (!msg) return;

    var inline = document.querySelector(
      '[data-error-message-wrapper]:not(.product-form__error-message-wrapper--hidden)'
    );
    if (inline && inline.offsetParent !== null) return;

    var toast = document.querySelector('.iac-cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'iac-cart-toast';
      toast.setAttribute('role', 'alert');
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    requestAnimationFrame(function () { toast.classList.add('visible'); });
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () { toast.classList.remove('visible'); }, 6000);
  });
})();
